import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Box, Send, Instagram, ChevronLeft, ChevronRight, Loader2, Tag } from 'lucide-react';
import ModelViewer from '../components/ModelViewer';
import PayPalCheckout from '../components/PayPalCheckout';
import ProductCard from '../components/ProductCard';

import { normalizeCmsImages, readSessionCache } from '../api/config';
import { resolveMediaUrl } from '../utils/media';
import { normalizeProduct, normalizeProducts } from '../utils/products';
import { cmsApi, productsApi, settingsApi } from '../api/requests';

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [settings, setSettings] = useState({ telegramUrl: '', instagramUrl: '' });
  const [cms, setCms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const productCacheKey = `product-cache-${slug}`;
      const cachedProduct = readSessionCache(productCacheKey);

      if (cachedProduct) {
        setProduct(normalizeProduct(cachedProduct));
      }

      try {
        const [productRes, settingsRes, cmsRes] = await Promise.all([
          productsApi.getBySlug(slug),
          settingsApi.getPublic(),
          cmsApi.getSection('product')
        ]);
        const normalizedProduct = normalizeProduct(productRes.data);

        setProduct(normalizedProduct);
        setSettings(settingsRes.data);
        setCms(normalizeCmsImages(cmsRes.data));
        setSelectedImage(0);
        sessionStorage.setItem(productCacheKey, JSON.stringify(productRes.data));

        const allProducts = await productsApi.getAll();
        const related = normalizeProducts(allProducts.data)
          .filter((p) => p.id !== normalizedProduct.id && p.category === normalizedProduct.category)
          .slice(0, 4);
        setRelatedProducts(related);
      } catch (error) {
        console.error('Failed to fetch product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  // CMS labels with defaults
  const backButtonText = cms?.backButtonText || 'Back to Collection';
  const priceLabel = cms?.priceLabel || 'Price';
  const materialLabel = cms?.materialLabel || 'Material';
  const sizeLabel = cms?.sizeLabel || 'Size';
  const descriptionTitle = cms?.descriptionTitle || 'Description';
  const buyButtonText = cms?.buyButtonText || 'Buy Now';
  const soldOutText = cms?.soldOutText || 'This item is currently sold out.';
  const notifyButtonText = cms?.notifyButtonText || 'Get Notified';
  const customOrderText = cms?.customOrderText || 'Want a custom version? We can create variations of this figurine.';
  const customOrderButtonText = cms?.customOrderButtonText || 'Discuss Custom Order';
  const relatedTitle = cms?.relatedTitle || 'Related Figurines';
  const telegramButtonText = cms?.telegramButtonText || 'Contact via Telegram';
  const instagramButtonText = cms?.instagramButtonText || 'View Instagram';
  const savingsText = cms?.savingsText || 'You save';

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="w-8 h-8 text-indigo-400 animate-spin" /></div>;
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-white mb-4">Product not found</h1>
          <Link to="/catalog" className="text-indigo-400 hover:text-indigo-300">{backButtonText}</Link>
        </div>
      </div>
    );
  }

  const allImages = product.images?.length > 0 ? product.images : [product.thumbnailImage];
  const stockStatusMap = {
    in_stock: { label: 'In Stock', color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
    made_to_order: { label: 'Made to Order', color: 'text-amber-400', bg: 'bg-amber-400/10' },
    sold_out: { label: 'Sold Out', color: 'text-red-400', bg: 'bg-red-400/10' },
  };
  const status = stockStatusMap[product.stockStatus] || stockStatusMap.in_stock;

  return (
    <div data-testid="product-detail-page" className="min-h-screen bg-[#0A0A0A] pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/catalog" data-testid="back-to-catalog"
          className="inline-flex items-center space-x-2 text-zinc-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /><span>{backButtonText}</span>
        </Link>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Column - Images/3D */}
          <div className="space-y-4">
            {product.glbModelUrl ? (
              <ModelViewer modelUrl={product.glbModelUrl} fallbackImage={allImages[0]} productTitle={product.title} />
            ) : (
              <div className="relative aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-zinc-900 to-black">
                <img src={resolveMediaUrl(allImages[selectedImage])} alt={product.title} className="w-full h-full object-cover" />
                {product.hasDiscount && (
                  <div className="absolute top-4 right-4 px-4 py-2 bg-red-500 rounded-full flex items-center space-x-2">
                    <Tag className="w-4 h-4 text-white" />
                    <span className="text-sm font-bold text-white">-{product.discountPercent}%</span>
                  </div>
                )}
                {allImages.length > 1 && (
                  <>
                    <button onClick={() => setSelectedImage((prev) => (prev - 1 + allImages.length) % allImages.length)}
                      data-testid="prev-image-btn"
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/60 backdrop-blur-sm rounded-full text-white hover:bg-black/80 transition-colors">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button onClick={() => setSelectedImage((prev) => (prev + 1) % allImages.length)}
                      data-testid="next-image-btn"
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/60 backdrop-blur-sm rounded-full text-white hover:bg-black/80 transition-colors">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
            )}
            {allImages.length > 1 && !product.glbModelUrl && (
              <div className="flex space-x-3 overflow-x-auto pb-2">
                {allImages.map((image, index) => (
                  <button key={index} onClick={() => setSelectedImage(index)} data-testid={`thumbnail-${index}`}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImage === index ? 'border-indigo-500' : 'border-transparent hover:border-white/30'
                    }`}>
                    <img src={resolveMediaUrl(image)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Details */}
          <div>
            <div className="mb-6">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-indigo-400 mb-2 block">{product.category}</span>
              <h1 data-testid="product-title" className="text-3xl lg:text-4xl font-['Outfit'] font-medium text-white mb-4">{product.title}</h1>
              <p className="text-zinc-400 text-lg">{product.shortDescription}</p>
            </div>

            <div className="flex items-center justify-between py-6 border-y border-white/10">
              <div>
                <span className="text-sm text-zinc-500">{priceLabel}</span>
                <div className="flex items-baseline gap-3">
                  <p data-testid="product-price" className="text-3xl font-['Outfit'] font-semibold text-white">&euro;{product.priceEUR.toFixed(2)}</p>
                  {product.hasDiscount && product.originalPrice && (
                    <p className="text-xl text-zinc-500 line-through">&euro;{product.originalPrice.toFixed(2)}</p>
                  )}
                </div>
                {product.hasDiscount && (
                  <p className="text-sm text-red-400 mt-1">{savingsText} &euro;{(product.originalPrice - product.priceEUR).toFixed(2)}</p>
                )}
              </div>
              <div className={`px-4 py-2 rounded-full ${status.bg}`}>
                <span data-testid="product-status" className={`text-sm font-medium ${status.color}`}>{status.label}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 py-6 border-b border-white/10">
              <div>
                <span className="text-sm text-zinc-500">{materialLabel}</span>
                <p data-testid="product-material" className="text-white">{product.material || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm text-zinc-500">{sizeLabel}</span>
                <p data-testid="product-size" className="text-white">{product.size || 'Not specified'}</p>
              </div>
              {product.glbModelUrl && (
                <div className="col-span-2">
                  <div className="flex items-center space-x-2 text-indigo-400">
                    <Box className="w-4 h-4" /><span className="text-sm">Interactive 3D Preview Available</span>
                  </div>
                </div>
              )}
            </div>

            {product.fullDescription && (
              <div className="py-6 border-b border-white/10">
                <h3 className="text-white font-medium mb-3 font-['Outfit']">{descriptionTitle}</h3>
                <p data-testid="product-description" className="text-zinc-400 leading-relaxed whitespace-pre-line">{product.fullDescription}</p>
              </div>
            )}

            <div className="py-6">
              {product.stockStatus === 'sold_out' ? (
                <div className="space-y-4">
                  <p className="text-zinc-400">{soldOutText}</p>
                  {settings.telegramUrl && (
                    <a href={settings.telegramUrl} target="_blank" rel="noopener noreferrer" data-testid="notify-telegram-btn"
                      className="inline-flex items-center space-x-2 px-6 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-medium rounded-lg transition-colors">
                      <Send className="w-4 h-4" /><span>{notifyButtonText}</span>
                    </a>
                  )}
                </div>
              ) : showCheckout ? (
                <PayPalCheckout product={product} />
              ) : (
                <button onClick={() => setShowCheckout(true)} data-testid="buy-now-btn"
                  className="w-full py-4 bg-indigo-500 hover:bg-indigo-600 text-white font-medium rounded-xl transition-colors text-lg">
                  {buyButtonText} - &euro;{product.priceEUR.toFixed(2)}
                </button>
              )}
            </div>

            {product.customOrderAvailable && (
              <div className="p-4 bg-white/5 border border-white/10 rounded-xl mb-6">
                <p className="text-zinc-400 text-sm mb-3">{customOrderText}</p>
                {settings.telegramUrl && (
                  <a href={settings.telegramUrl} target="_blank" rel="noopener noreferrer" data-testid="custom-order-btn"
                    className="inline-flex items-center space-x-2 text-indigo-400 hover:text-indigo-300 font-medium">
                    <Send className="w-4 h-4" /><span>{customOrderButtonText}</span>
                  </a>
                )}
              </div>
            )}

            <div className="flex items-center space-x-4">
              {settings.telegramUrl && (
                <a href={settings.telegramUrl} target="_blank" rel="noopener noreferrer" data-testid="product-telegram"
                  className="flex items-center space-x-2 px-4 py-2 bg-white/5 hover:bg-indigo-500/20 border border-white/10 hover:border-indigo-500/50 rounded-lg transition-all text-zinc-400 hover:text-white">
                  <Send className="w-4 h-4" /><span className="text-sm">{telegramButtonText}</span>
                </a>
              )}
              {settings.instagramUrl && (
                <a href={settings.instagramUrl} target="_blank" rel="noopener noreferrer" data-testid="product-instagram"
                  className="flex items-center space-x-2 px-4 py-2 bg-white/5 hover:bg-indigo-500/20 border border-white/10 hover:border-indigo-500/50 rounded-lg transition-all text-zinc-400 hover:text-white">
                  <Instagram className="w-4 h-4" /><span className="text-sm">{instagramButtonText}</span>
                </a>
              )}
            </div>
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <div className="mt-24">
            <h2 className="text-2xl font-['Outfit'] font-medium text-white mb-8">{relatedTitle}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
